import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Button,
  CircularProgress,
  FormHelperText,
  Box,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import RefreshIcon from "@mui/icons-material/Refresh";
import api from "../../utils/api";

export default function AddModal({ open, handleClose }) {
  const [formData, setFormData] = useState({
    ID_Site: "",
    matricule: "",
    id_pickup: "", // zone in React = id_pickup in Laravel
    prenom: "",
    name: "", // nom in React = name in Laravel
    telephone: "",
    adresse: "",
    ID_Role: "",
    email: "",
    password: "", // motDePasse in React = password in Laravel
    password_confirmation: "", // confirmation in React = password_confirmation in Laravel
    Status: "Actif", // Default status is active
    adhesion: false, // Nouveau champ - peut être null
    date_adhesion: "", // Nouveau champ - peut être null
    latitude: "", // Nouveau champ - peut être null
    longitude: "", // Nouveau champ - peut être null
    Uid: "", // Nouveau champ - peut être null
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  // State for sites, roles, and pickup zones
  const [sites, setSites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pickupZones, setPickupZones] = useState([]);
  
  // Loading states
  const [sitesLoading, setSitesLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [pickupZonesLoading, setPickupZonesLoading] = useState(true);
  
  // Error states
  const [sitesError, setSitesError] = useState(false);
  const [rolesError, setRolesError] = useState(false);
  const [pickupZonesError, setPickupZonesError] = useState(false);
  
  // Fetch sites from API
  const fetchSites = async (showErrorMessage = true) => {
    setSitesLoading(true);
    setSitesError(false);
    try {
      const response = await api.get('/sites');
      setSites(response.data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
      setSitesError(true);
      if (showErrorMessage) {
        // You could add a snackbar notification here if needed
      }
    } finally {
      setSitesLoading(false);
    }
  };
  
  // Retry fetching sites
  const retryFetchSites = () => {
    fetchSites(true);
  };
  
  // Fetch roles from API
  const fetchRoles = async (showErrorMessage = true) => {
    setRolesLoading(true);
    setRolesError(false);
    try {
      const response = await api.get('/roles');
      setRoles(response.data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setRolesError(true);
      if (showErrorMessage) {
        // You could add a snackbar notification here if needed
      }
    } finally {
      setRolesLoading(false);
    }
  };
  
  // Retry fetching roles
  const retryFetchRoles = () => {
    fetchRoles(true);
  };
  
  // Fetch pickup zones from API
  const fetchPickupZones = async (showErrorMessage = true) => {
    setPickupZonesLoading(true);
    setPickupZonesError(false);
    try {
      const response = await api.get('/pickups'); // Assuming this is the correct endpoint
      setPickupZones(response.data || []);
    } catch (error) {
      console.error('Error fetching pickup zones:', error);
      setPickupZonesError(true);
      if (showErrorMessage) {
        // You could add a snackbar notification here if needed
      }
    } finally {
      setPickupZonesLoading(false);
    }
  };
  
  // Retry fetching pickup zones
  const retryFetchPickupZones = () => {
    fetchPickupZones(true);
  };
  
  // Fetch sites, roles, and pickup zones on component mount
  useEffect(() => {
    if (open) {
      fetchSites();
      fetchRoles();
      fetchPickupZones();
    }
  }, [open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    // Clear error for this field when user makes a change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    // ID_Site is nullable
    if (!formData.matricule) newErrors.matricule = 'Le matricule est requis';
    if (!formData.name) newErrors.name = 'Le nom est requis';
    if (!formData.prenom) newErrors.prenom = 'Le prénom est requis';
    if (!formData.email) newErrors.email = 'L\'email est requis';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Format d\'email invalide';
    
    if (!formData.password) newErrors.password = 'Le mot de passe est requis';
    else if (formData.password.length < 8) newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    
    if (formData.password !== formData.password_confirmation) {
      newErrors.password_confirmation = 'Les mots de passe ne correspondent pas';
    }
    
    // ID_Role is nullable
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      await api.post('/users/create', formData);
      resetForm();
      handleClose();
    } catch (error) {
      console.error('Error creating user:', error);
      
      // Handle validation errors from the server
      if (error.response && error.response.data) {
        if (error.response.data.errors) {
          // Process specific validation errors
          setErrors(error.response.data.errors);
        } else if (error.response.data.message) {
          // Set general error message
          setErrors({ general: error.response.data.message });
        } else {
          setErrors({ general: 'Erreur lors de la création de l\'utilisateur' });
        }
      } else {
        setErrors({ general: 'Erreur de connexion au serveur' });
      }
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setFormData({
      ID_Site: "",
      matricule: "",
      id_pickup: "",
      prenom: "",
      name: "",
      telephone: "",
      adresse: "",
      ID_Role: "",
      email: "",
      password: "",
      password_confirmation: "",
      Status: "Actif",
      adhesion: false,
      date_adhesion: "",
      latitude: "",
      longitude: "",
      Uid: "",
    });
    setErrors({});
  };

  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      height: 35,
      '&:hover fieldset': { borderColor: '#000' },
      '&.Mui-focused fieldset': { borderColor: 'red' }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 400, mr: 3 }}>
          AJOUTER UN UTILISATEUR
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        )}
        <Typography variant="subtitle2">Site </Typography>
        <Box display="flex" alignItems="center" width="100%">
          <FormControl fullWidth sx={inputStyle} error={!!errors.ID_Site}>
            <Select 
              name="ID_Site" 
              value={formData.ID_Site} 
              onChange={handleChange}
              disabled={sitesLoading}
            >
              <MenuItem value="">Sélectionner un site</MenuItem>
              {sitesLoading ? (
                <MenuItem disabled>Chargement des sites...</MenuItem>
              ) : sitesError ? (
                <MenuItem disabled sx={{ color: 'error.main' }}>
                  Erreur de chargement
                </MenuItem>
              ) : (
                sites.map(site => (
                  <MenuItem key={site.ID_Site} value={site.ID_Site}>{site.name}</MenuItem>
                ))
              )}
            </Select>
            {errors.ID_Site && <FormHelperText error>{errors.ID_Site}</FormHelperText>}
          </FormControl>
          <IconButton 
            onClick={retryFetchSites} 
            disabled={sitesLoading}
            sx={{ ml: 1, color: sitesError ? 'error.main' : 'inherit' }}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        <Typography variant="subtitle2">Matricule</Typography>
        <TextField 
          fullWidth 
          type="text" 
          name="matricule" 
          value={formData.matricule} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.matricule}
          helperText={errors.matricule}
        />


        <Typography variant="subtitle2">Prénom</Typography>
        <TextField 
          fullWidth 
          type="text" 
          name="prenom" 
          value={formData.prenom} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.prenom}
          helperText={errors.prenom}
        />

        <Typography variant="subtitle2">Nom</Typography>
        <TextField 
          fullWidth 
          type="text" 
          name="name" 
          value={formData.name} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.name}
          helperText={errors.name}
        />

        <Typography variant="subtitle2">Téléphone</Typography>
        <TextField 
          fullWidth 
          type="text" 
          name="telephone" 
          value={formData.telephone} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.telephone}
          helperText={errors.telephone}
        />

        <Typography variant="subtitle2">Adresse</Typography>
        <TextField 
          fullWidth 
          type="text" 
          name="adresse" 
          value={formData.adresse} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.adresse}
          helperText={errors.adresse}
        />

        <Typography variant="subtitle2">Rôle </Typography>
        <Box display="flex" alignItems="center" width="100%">
          <FormControl fullWidth sx={inputStyle} error={!!errors.ID_Role}>
            <Select 
              name="ID_Role" 
              value={formData.ID_Role} 
              onChange={handleChange}
              disabled={rolesLoading}
            >
              <MenuItem value="">Sélectionner un rôle</MenuItem>
              {rolesLoading ? (
                <MenuItem disabled>Chargement des rôles...</MenuItem>
              ) : rolesError ? (
                <MenuItem disabled sx={{ color: 'error.main' }}>
                  Erreur de chargement
                </MenuItem>
              ) : (
                roles.map(role => (
                  <MenuItem key={role.ID_Role} value={role.ID_Role}>{role.Role}</MenuItem>
                ))
              )}
            </Select>
            {errors.ID_Role && <FormHelperText error>{errors.ID_Role}</FormHelperText>}
          </FormControl>
          <IconButton 
            onClick={retryFetchRoles} 
            disabled={rolesLoading}
            sx={{ ml: 1, color: rolesError ? 'error.main' : 'inherit' }}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        <Typography variant="subtitle2">Email</Typography>
        <TextField 
          fullWidth 
          type="email" 
          name="email" 
          value={formData.email} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.email}
          helperText={errors.email}
        />

        <Typography variant="subtitle2">Mot de passe</Typography>
        <TextField 
          fullWidth 
          type="password" 
          name="password" 
          value={formData.password} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.password}
          helperText={errors.password}
        />

        <Typography variant="subtitle2">Confirmation</Typography>
        <TextField 
          fullWidth 
          type="password" 
          name="password_confirmation" 
          value={formData.password_confirmation} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.password_confirmation}
          helperText={errors.password_confirmation}
        />

        <Typography variant="subtitle2">Adhésion</Typography>
        <FormControl fullWidth sx={inputStyle} error={!!errors.adhesion}>
          <Select
            name="adhesion"
            value={formData.adhesion}
            onChange={handleChange}
          >
            <MenuItem value={true}>Oui</MenuItem>
            <MenuItem value={false}>Non</MenuItem>
            <MenuItem value="">Non spécifié</MenuItem>
          </Select>
          {errors.adhesion && <FormHelperText error>{errors.adhesion}</FormHelperText>}
        </FormControl>

        <Typography variant="subtitle2">Date d'adhésion</Typography>
        <TextField 
          fullWidth 
          type="date" 
          name="date_adhesion" 
          value={formData.date_adhesion} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.date_adhesion}
          helperText={errors.date_adhesion}
          InputLabelProps={{ shrink: true }}
        />

        <Typography variant="subtitle2">Latitude</Typography>
        <TextField 
          fullWidth 
          type="number" 
          name="latitude" 
          value={formData.latitude} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.latitude}
          helperText={errors.latitude}
        />

        <Typography variant="subtitle2">Longitude</Typography>
        <TextField 
          fullWidth 
          type="number" 
          name="longitude" 
          value={formData.longitude} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.longitude}
          helperText={errors.longitude}
        />

        <Typography variant="subtitle2">UID</Typography>
        <TextField 
          fullWidth 
          type="text" 
          name="Uid" 
          value={formData.Uid} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.Uid}
          helperText={errors.Uid}
        />
      </DialogContent>
      <Divider sx={{ mb: 1 }} />
      <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button 
          variant="outlined" 
          sx={{ color: "red", borderColor: "red", textTransform: "none", "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } }} 
          startIcon={<CloseIcon />} 
          onClick={handleClose}
          disabled={loading}
        >
          Close
        </Button>
        <Button 
          variant="outlined" 
          sx={{ color: "red", borderColor: "red", textTransform: "none", "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } }} 
          startIcon={loading ? <CircularProgress size={20} color="error" /> : <CheckIcon />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Enregistrement...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}